import {
  ArrowLeft,
  Mail,
  Edit2,
  Trash2,
  Loader2,
  Save,
  X,
  Send,
  ChevronDown,
  Receipt,
  FileSignature,
  FileText,
} from "lucide-react";
import type { Customer, Order } from "./types";

interface CustomerDetailHeaderProps {
  customer: Customer;
  isEditing: boolean;
  isPendingUpdate: boolean;
  isPendingDelete: boolean;
  showActionsMenu: boolean;
  onNavigateBack: () => void;
  onSetShowActionsMenu: (show: boolean) => void;
  onOpenSendModal: (type: "email" | "pdf", order?: Order) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
}

export default function CustomerDetailHeader({
  customer,
  isEditing,
  isPendingUpdate,
  isPendingDelete,
  showActionsMenu,
  onNavigateBack,
  onSetShowActionsMenu,
  onOpenSendModal,
  onEdit,
  onSave,
  onCancelEdit,
  onDelete,
}: CustomerDetailHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <button onClick={onNavigateBack} className="btn-ghost btn-sm">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-4">
          <div className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 flex h-14 w-14 items-center justify-center rounded-full text-xl font-bold">
            {customer.firstName?.[0]}
            {customer.lastName?.[0]}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {customer.firstName} {customer.lastName}
            </h1>
            <p className="text-gray-500">{customer.email}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <button onClick={onCancelEdit} className="btn-ghost">
              <X className="mr-2 h-4 w-4" />
              Cancel
            </button>
            <button onClick={onSave} disabled={isPendingUpdate} className="btn-primary">
              {isPendingUpdate ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </button>
          </>
        ) : (
          <>
            {/* Quick Actions Dropdown */}
            <div className="relative">
              <button
                onClick={() => onSetShowActionsMenu(!showActionsMenu)}
                className="btn-primary"
              >
                <Send className="mr-2 h-4 w-4" />
                Send
                <ChevronDown className="ml-2 h-4 w-4" />
              </button>

              {showActionsMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => onSetShowActionsMenu(false)} />
                  <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <div className="border-b border-gray-200 px-3 py-2 dark:border-gray-700">
                      <p className="text-xs font-medium uppercase text-gray-500">Emails</p>
                    </div>
                    <button
                      onClick={() => onOpenSendModal("email")}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <Mail className="h-4 w-4" />
                      Send email
                    </button>
                    <button
                      onClick={() => onOpenSendModal("email")}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <Receipt className="h-4 w-4" />
                      Send confirmation
                    </button>

                    <div className="border-b border-t border-gray-200 px-3 py-2 dark:border-gray-700">
                      <p className="text-xs font-medium uppercase text-gray-500">Documents</p>
                    </div>
                    <button
                      onClick={() => onOpenSendModal("pdf")}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <FileSignature className="h-4 w-4" />
                      Send contract
                    </button>
                    <button
                      onClick={() => onOpenSendModal("pdf")}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <FileText className="h-4 w-4" />
                      Send invoice
                    </button>
                  </div>
                </>
              )}
            </div>

            <button onClick={onEdit} className="btn-ghost">
              <Edit2 className="mr-2 h-4 w-4" />
              Edit
            </button>
            <button
              onClick={onDelete}
              disabled={isPendingDelete}
              className="btn-ghost text-error-600 hover:bg-error-50"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
}
